/**
 * SendToPaintModal — operator dialog for creating a PaintRequest batch.
 *
 * Phase B's create endpoint enforces "sent immediately" semantics — submit
 * → 201 → PR# issued + delivery ticket queued + qty_sent_to_paint bumped
 * in one atomic transaction. No draft state, no client-side persistence.
 */

import { useEffect, useMemo, useState } from 'react';
import Modal from '../../common/Modal';
import { createPaintRequest } from '../../../api/paintRequests';
import { PaintStatusBadge } from './PaintActivityPanel';

function fmtQty(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n % 1 === 0 ? String(Math.round(n)) : n.toString();
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export default function SendToPaintModal({
  open, onClose, onSuccess, line, job, pastBatches = [],
}) {
  const uom = line?.part_uom || 'each';
  const qtyRemaining = useMemo(() => {
    const n = parseFloat(line?.qty_remaining);
    return isNaN(n) ? 0 : Math.max(n, 0);
  }, [line]);
  const qtySent = parseFloat(line?.qty_sent_to_paint) || 0;

  const [qty, setQty] = useState('');
  const [color, setColor] = useState('');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setQty(qtyRemaining > 0 ? String(fmtQty(qtyRemaining)) : '');
    setColor(job?.paint_color || '');
    setInstructions('');
    setError('');
    setSubmitting(false);
  }, [open, qtyRemaining, job?.paint_color]);

  const qtyNum = parseFloat(qty);
  const colorTrimmed = color.trim();
  const isValid = !isNaN(qtyNum) && qtyNum > 0 && colorTrimmed.length > 0;
  const colorPrefilled = Boolean(job?.paint_color) && colorTrimmed === (job?.paint_color || '').trim();

  async function handleSubmit() {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const pr = await createPaintRequest({
        job_bom_line_id: line.id,
        qty_sent: qtyNum,
        paint_color: colorTrimmed,
        special_instructions: instructions.trim(),
      });
      onSuccess?.(pr);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        setError('Paint service is not enabled. Contact an admin.');
      } else {
        setError(detail || 'Failed to send to paint.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={`Send to Paint — Job ${job.job_number}${job.customer_name ? ` (${job.customer_name})` : ''}`}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Read-only context */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 bg-gray-50 border border-gray-200 rounded p-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">Part</div>
            <div className="text-sm font-medium text-gray-800">{line.part_display || '—'}</div>
            {line.part_description && (
              <div className="text-xs text-gray-500 truncate">{line.part_description}</div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">Vendor</div>
            <div className="text-sm text-gray-800">{line.vendor_part_display || '— no vendor assigned —'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">Pump system part #</div>
            <div className="text-sm font-mono text-gray-800">{job.parent_item_no || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">Still needed for build</div>
            <div className="text-sm text-gray-800">{fmtQty(qtyRemaining)} {uom}</div>
            {qtySent > 0 && (
              <div className="text-xs text-gray-500">
                Already sent to paint: {fmtQty(qtySent)} {uom}
              </div>
            )}
          </div>
        </div>

        {/* Form fields */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity to send <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="any"
                className="w-32 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                onFocus={(e) => e.target.select()}
                disabled={submitting}
              />
              <span className="text-sm text-gray-500">{uom}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paint color <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              placeholder="e.g., RAL 9010 White"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              disabled={submitting}
            />
            {colorPrefilled && (
              <p className="text-xs text-gray-500 mt-1">
                Pre-filled from job settings — edit if this batch differs.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Special instructions <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              placeholder="e.g., Mask threaded ports before painting"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        {/* Past batches */}
        {pastBatches.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Previous batches for this line
            </div>
            <div className="border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Date</th>
                    <th className="px-2 py-1.5 text-left">PR#</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-left">Color</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pastBatches.map((pr) => (
                    <tr key={pr.id} className="text-gray-700">
                      <td className="px-2 py-1.5">{fmtDate(pr.sent_at)}</td>
                      <td className="px-2 py-1.5 font-mono text-blue-700">{pr.paint_request_number}</td>
                      <td className="px-2 py-1.5 text-right">{fmtQty(pr.qty_sent)}</td>
                      <td className="px-2 py-1.5">{pr.paint_color}</td>
                      <td className="px-2 py-1.5"><PaintStatusBadge status={pr.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
        <button
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Sending…' : '🎨 Send to Paint'}
        </button>
      </div>
    </Modal>
  );
}
